import {skipToken} from '@reduxjs/toolkit/query'
import {useCallback, useMemo, useRef, useState} from 'react'

import {useAppDispatch, useAppSelector} from '../../../../../../../hooks/react-redux'
import {emptyArray, emptyObject} from '../../../../../../../utils/empty'
import {usePipelineId} from '../../../../hooks/pipeline'
import {pipelinesApi} from '../../../../services/pipelinesApi'
import type {VcsRootDto, VersionedSettingsDto} from '../../../../services/pipelinesApi.types'
import type {Job} from '../../../../types'
import {isSpaceConnectionEnabled} from '../../../../utils/featureToggles'
import {
  getDraft,
  getDraftJobs,
  getOriginalVersionedSettings,
} from '../../../EditPipelinePage.selectors'
import {collapsedBlocks, pipelineDraft} from '../../../slices/EditPipelinePage.slices'

import type {PipelineBaseRepository} from './PipelineRepositories.types'
import {getGroupRepositoriesFlat} from './PipelineRepositories.utils'

export const pipelineRepositorySettingsBlockId = 'repositories'
const mainRepositoryName = 'main'

function useRepositoryGroups() {
  const {gitHubRepositoryGroups} = pipelinesApi.endpoints.getGitHubRepositories.useQuery(
    isSpaceConnectionEnabled ? skipToken : undefined,
    {
      selectFromResult: ({data}) => ({
        gitHubRepositoryGroups: data,
      }),
    },
  )
  const {spaceRepositoryGroups} = pipelinesApi.endpoints.getSpaceRepositories.useQuery(
    isSpaceConnectionEnabled ? undefined : skipToken,
    {
      selectFromResult: ({data}) => ({
        spaceRepositoryGroups: data,
      }),
    },
  )
  return isSpaceConnectionEnabled ? spaceRepositoryGroups : gitHubRepositoryGroups
}

export function usePipelineRepositories() {
  const pipelineId = usePipelineId()
  const dispatch = useAppDispatch()
  const deletingVcsRootsUrl = useRef<string | null>(null)
  const [deletingJobsRepository, setSeletingJobsRepository] = useState<string>('')
  const [isShowDeleteJobConfirmPopup, setShowDeleteJobConfirmPopup] = useState<boolean>(false)
  const [isCreateMode, setCreateMode] = useState<boolean>(false)
  const mainRepository =
    useAppSelector(state => getDraft(state, pipelineId)?.vcsRoot) ?? (emptyObject as VcsRootDto)
  const additionalVcsRoots =
    useAppSelector(state => getDraft(state, pipelineId)?.additionalVcsRoots) ?? emptyArray
  const versionedSettings = useAppSelector(state => getDraft(state, pipelineId)?.versionedSettings)
  const savedVersionedSettings = useAppSelector(getOriginalVersionedSettings(pipelineId))
  const repositoryGroups = useRepositoryGroups()
  const createdVcsRoots = useMemo(
    () => [mainRepository, ...additionalVcsRoots],
    [additionalVcsRoots, mainRepository],
  )
  const createdRepositories: Set<string> = useMemo(
    () => new Set(createdVcsRoots?.map(({url}) => url)),
    [createdVcsRoots],
  )
  const repositoriesFlat = useMemo(
    () => getGroupRepositoriesFlat(repositoryGroups ?? []),
    [repositoryGroups],
  )
  const addRepositoryHandler = () => {
    setCreateMode(true)
    dispatch(
      collapsedBlocks.actions.toggle({id: pipelineRepositorySettingsBlockId, collapsed: false}),
    )
  }
  const closeCreateRepositoryFormHandler = () => setCreateMode(false)
  const updateMainRepositoryHandler = (vcsRoot: PipelineBaseRepository) => {
    dispatch(pipelineDraft.actions.updateMainVcsRoot({id: pipelineId, ...vcsRoot}))
  }
  const changeRepositoryHandler = (index: number) => (vcsRoot: PipelineBaseRepository) =>
    dispatch(
      pipelineDraft.actions.updateAdditionalVcsRoot({
        id: pipelineId,
        index,
        ...vcsRoot,
      }),
    )
  const createRepositoryHandler = (vcsRoot: PipelineBaseRepository) => {
    dispatch(
      pipelineDraft.actions.setAdditionalVcsRoot({
        id: pipelineId,
        vcsName: 'jetbrains.git',
        ...vcsRoot,
      }),
    )
    setCreateMode(false)
  }
  const draftJobs = (useAppSelector(state => getDraftJobs(state, pipelineId)) ??
    emptyObject) as Record<string, Job>
  const deleteAdditionalVcsRootHandler = (url: string | null) => {
    if (url) {
      dispatch(
        pipelineDraft.actions.deleteAdditionalVcsRoot({
          id: pipelineId,
          url,
        }),
      )
      deletingVcsRootsUrl.current = null
      setShowDeleteJobConfirmPopup(false)
    }
  }
  const deleteRepositoryHandler = (url: string) => () => {
    // Ask about switching the repository to the main if the deleted repo was the last
    const usedJobs = Object.entries(draftJobs).reduce<string[]>((acc, [jobId, value]) => {
      const jobRepositories = value.repositories ?? []
      const repositoriesKeysSet = new Set(
        jobRepositories.flatMap(repository => Object.keys(repository)),
      )
      const isMainRepositoryDisabled = repositoriesKeysSet.has(mainRepositoryName)
      const hasUrlRepositories = repositoriesKeysSet.has(url)
      const isCanDeleted =
        isMainRepositoryDisabled && hasUrlRepositories && repositoriesKeysSet.size === 2

      if (isCanDeleted) {
        acc.push(value?.name ?? jobId)
      }

      return acc
    }, [])

    if (usedJobs.length) {
      deletingVcsRootsUrl.current = url
      setShowDeleteJobConfirmPopup(true)
      setSeletingJobsRepository(usedJobs.join(', '))
    } else {
      deletingVcsRootsUrl.current = null
      deleteAdditionalVcsRootHandler(url)
    }
  }
  const restoreRepositoryHandler = (url: string) => () =>
    dispatch(
      pipelineDraft.actions.restoreAdditionalVcsRoot({
        id: pipelineId,
        url,
      }),
    )
  const deletedRepositories = useAppSelector(
    state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.additionalVcsRoots,
  )
  const deletedRepositoriesSet = useMemo(() => new Set(deletedRepositories), [deletedRepositories])
  const shortRepositories = useMemo(
    () => createdVcsRoots.map(({url, branch}) => `${url} Default branches: ${branch}`).join('; '),
    [createdVcsRoots],
  )
  const clickConfirmDeleteJobButtonHandler = () =>
    deleteAdditionalVcsRootHandler(deletingVcsRootsUrl.current)
  const clickRejectDeleteJobButtonHandler = () => {
    setShowDeleteJobConfirmPopup(false)
    deletingVcsRootsUrl.current = null
  }
  const changeVersionedSettingsHandler = useCallback(
    (settings: VersionedSettingsDto) => {
      dispatch(
        pipelineDraft.actions.setVersionedSettings({
          id: pipelineId,
          ...settings,
        }),
      )
    },
    [dispatch, pipelineId],
  )
  const confirmDeleteJobDescription = `This repository is used by the following Job(s): ${deletingJobsRepository}. If you remove it, all repositories that used it will switch to the default (${mainRepository.url}) repository.`

  return {
    additionalVcsRoots,
    deletedRepositoriesSet,
    shortRepositories,
    isCreateMode,
    mainRepository,
    repositoriesFlat,
    createdRepositories,
    isShowDeleteJobConfirmPopup,
    confirmDeleteJobDescription,
    savedVersionedSettings,
    versionedSettings,
    addRepositoryHandler,
    closeCreateRepositoryFormHandler,
    updateMainRepositoryHandler,
    deleteRepositoryHandler,
    restoreRepositoryHandler,
    changeRepositoryHandler,
    createRepositoryHandler,
    clickConfirmDeleteJobButtonHandler,
    clickRejectDeleteJobButtonHandler,
    changeVersionedSettingsHandler,
  }
}
